/**
 * PulseBotPanel.tsx — the dockable PulseBot chat widget.
 *
 * A bottom-right docked chat panel (mirrors CommandPalette's overlay chrome).
 *
 * Phase 1A (read): input is routed through src/lib/pulsebot/router.ts to
 * deterministic READ-ONLY handlers — no LLM, no writes.
 *
 * Phase 1B + 2 (write): create_deal and create_ticket. A trigger phrase starts a
 * multi-turn slot-filling DIALOG whose state machine lives here — a discriminated
 * union on `kind` ('deal' | 'ticket') carrying the current slot + collected
 * values. Pickers bind values (deal: real contact/owner row IDs; ticket: fixed
 * enums) and NOTHING is written until the user clicks Create
 * (confirm-before-execute). The pure logic + data layer is
 * src/lib/pulsebot/dialog.ts. While a dialog is active, input and picker clicks
 * drive the slot machine; with no dialog active, input falls through to the
 * Phase 1A read router (which still works unchanged).
 *
 * Exports:
 *   - default PulseBotPanel — the chat panel (renders null when closed)
 *   - PulseBotFAB           — the floating open/close button (always mounted)
 *
 * Both read open-state from usePulseBot(); the panel reads the current user +
 * company from useAuth()/useModules() to build the handler context, the
 * owner-default ("You"), and the ticket requester_id.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Send, X, Loader2, Sparkles, RotateCcw } from 'lucide-react';
import { usePulseBot } from '../../context/PulseBotContext';
import { useAuth } from '../../context/AuthContext';
import { useModules } from '../../context/ModuleContext';
import { runPulseBot, matchIntent } from '../../lib/pulsebot/router';
import Markdown from './Markdown';
import { resetLlmConversation } from '../../lib/pulsebot/llm';
import type { BotResultItem } from '../../lib/pulsebot/types';
import {
  // dialog commands
  isCancelInput,
  // create_deal (Phase 1B)
  isCreateDealTrigger,
  executeCreateDeal,
  summarizeDeal,
  stageLabel,
  fetchContactOptions,
  fetchOwnerOptions,
  STAGE_OPTIONS,
  DEFAULT_STAGE,
  type CollectedDeal,
  type DealSlot,
  type StageOption,
  type PickerOption,
  // create_ticket (Phase 2)
  isCreateTicketTrigger,
  executeCreateTicket,
  summarizeTicket,
  priorityLabel,
  categoryLabel,
  PRIORITY_OPTIONS,
  DEFAULT_PRIORITY,
  CATEGORY_OPTIONS,
  DEFAULT_CATEGORY,
  type CollectedTicket,
  type TicketSlot,
  type PriorityOption,
  type CategoryOption,
  // create_invoice (Phase 2)
  isCreateInvoiceTrigger,
  executeCreateInvoice,
  summarizeInvoice,
  addDaysISODate,
  type CollectedInvoice,
  type InvoiceSlot,
  // create_company (Phase 2)
  isCreateCompanyTrigger,
  executeCreateCompany,
  summarizeCompany,
  INDUSTRY_OPTIONS,
  DEFAULT_INDUSTRY,
  type CollectedCompany,
  type CompanySlot,
  type IndustryOption,
  // add_contact (Phase 2)
  isAddContactTrigger,
  executeAddContact,
  summarizeContact,
  contactTypeLabel,
  CONTACT_TYPE_OPTIONS,
  DEFAULT_CONTACT_TYPE,
  type CollectedContact,
  type ContactSlot,
  type ContactTypeOption,
  // request_leave (Phase 2)
  isRequestLeaveTrigger,
  executeRequestLeave,
  summarizeLeave,
  leaveTypeLabel,
  workingDays,
  isValidISODate,
  LEAVE_TYPE_OPTIONS,
  DEFAULT_LEAVE_TYPE,
  type CollectedLeave,
  type LeaveSlot,
  type LeaveTypeOption,
  // request_invite (Phase 4)
  isRequestInviteTrigger,
  executeRequestInvite,
  summarizeInvite,
  suggestedRoleLabel,
  isLikelyEmail,
  fetchDepartmentOptions,
  SUGGESTABLE_ROLE_OPTIONS,
  DEFAULT_SUGGESTED_ROLE,
  type CollectedInvite,
  type InviteSlot,
  type RoleOption,
} from '../../lib/pulsebot/dialog';
import { canCreateInvoice, canRequestInvite } from '../../lib/pulsebot/permissions';

// ─── Message model ──────────────────────────────────────────────────────────

/**
 * An interactive control rendered inline under a bot message. A control is
 * INTERACTIVE only while the dialog's current slot matches it (see renderControl)
 * — once answered it goes inert, leaving the user's echo message as the record.
 */
type BotControl =
  | { kind: 'stage' }
  | { kind: 'contact' }
  | { kind: 'owner'; defaultId: string | null; defaultLabel: string | null }
  | { kind: 'priority' }
  | { kind: 'category' }
  | { kind: 'industry' }
  | { kind: 'contactType' }
  | { kind: 'leaveType' }
  | { kind: 'role' }
  | { kind: 'department' }
  | { kind: 'confirm'; summary: string };

interface ChatMessage {
  id: number;
  role: 'user' | 'bot';
  text: string;
  /** Render `text` as markdown. Set only for LLM-fallback answers. */
  markdown?: boolean;
  items?: BotResultItem[];
  control?: BotControl;
  link?: { to: string; label: string };
}

/**
 * The active write dialog. Discriminated on `kind` so the SAME state machine
 * drives both executors: each variant carries its own slot enum + collected shape.
 */
type DialogState =
  | { kind: 'deal'; slot: DealSlot; collected: CollectedDeal }
  | { kind: 'ticket'; slot: TicketSlot; collected: CollectedTicket }
  | { kind: 'invoice'; slot: InvoiceSlot; collected: CollectedInvoice }
  | { kind: 'company'; slot: CompanySlot; collected: CollectedCompany }
  | { kind: 'contact'; slot: ContactSlot; collected: CollectedContact }
  | { kind: 'leave'; slot: LeaveSlot; collected: CollectedLeave }
  | { kind: 'invite'; slot: InviteSlot; collected: CollectedInvite };

const GREETING_TEXT =
  'Hi! I\'m PulseBot. Type "help" to see everything I can do — or try '
  + '"my open tickets" or "create deal".';

const isSkip = (text: string) => /^skip$/i.test(text.trim());

// NOTE: The separate bottom-right PulseBot FAB was merged into the combined
// "Help & Assistant" hub (HelpWidget). The bot now opens from that hub's
// "Ask PulseBot" item or the ⌘J shortcut; this file exports only the panel.

// ─── Result-item card ───────────────────────────────────────────────────────

function ResultItem({ item, onNavigate }: { item: BotResultItem; onNavigate?: () => void }) {
  const inner = (
    <div className="card !p-2.5 !rounded-lg flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-white/90 text-xs font-medium leading-snug truncate">{item.label}</p>
        {item.sublabel && (
          // KB snippets (linked items) wrap to 2 lines; compact read rows stay 1-line.
          <p className={`text-white/40 text-[11px] mt-0.5 ${item.link ? 'line-clamp-2' : 'truncate'}`}>
            {item.sublabel}
          </p>
        )}
      </div>
      {item.badge && (
        <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium capitalize bg-teal-500/20 text-teal-300">
          {item.badge}
        </span>
      )}
    </div>
  );

  // An item with a link renders as a clickable row (navigates + closes the panel,
  // same mechanism as the message-level link). Linkless items render unchanged.
  if (item.link) {
    return (
      <Link to={item.link} onClick={onNavigate} className="block hover:opacity-90 transition-opacity">
        {inner}
      </Link>
    );
  }
  return inner;
}

// ─── Dialog pickers ────────────────────────────────────────────────────────

/**
 * Fixed-enum picker — a row of buttons with the default value highlighted. Used
 * for the deal stage and the ticket priority/category (no async fetch). `T` is
 * the enum value type so onPick returns the exact-typed option.
 */
function EnumPicker<T extends string>({
  options, defaultValue, onPick,
}: {
  options: { value: T; label: string }[];
  defaultValue: T;
  onPick: (opt: { value: T; label: string }) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onPick(opt)}
          className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
            opt.value === defaultValue
              ? 'border-teal-500/40 bg-teal-500/15 text-teal-200'
              : 'border-white/10 text-white/70 hover:bg-white/10'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Picker that fetches its own rows on mount (contacts or profiles). Shows a
 * filter input once there are more than 8 rows, lists up to 8 matches, and a
 * Skip button. `skipLabel` lets the owner picker say "Skip (use me)".
 */
function RowPicker({
  load, onPick, onSkip, skipLabel, emptyText, header,
}: {
  load: () => Promise<PickerOption[]>;
  onPick: (opt: PickerOption) => void;
  onSkip: () => void;
  skipLabel: string;
  emptyText: string;
  /** Optional pinned row rendered above the list (e.g. owner default "You"). */
  header?: { option: PickerOption; note: string };
}) {
  const [options, setOptions] = useState<PickerOption[] | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let alive = true;
    load().then(opts => { if (alive) setOptions(opts); });
    return () => { alive = false; };
    // load is a stable module fn passed per-render; intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (options === null) {
    return (
      <div className="mt-2 text-white/40 text-xs flex items-center gap-1.5">
        <Loader2 size={12} className="animate-spin" /> Loading…
      </div>
    );
  }

  const q = filter.toLowerCase().trim();
  // The pinned header row (owner default) is excluded from the filtered list so
  // it isn't shown twice.
  const base = header ? options.filter(o => o.id !== header.option.id) : options;
  const filtered = q
    ? base.filter(o => o.label.toLowerCase().includes(q) || (o.sublabel?.toLowerCase().includes(q) ?? false))
    : base;

  return (
    <div className="mt-2 space-y-1.5">
      {header && (
        <button
          type="button"
          onClick={() => onPick(header.option)}
          className="card !p-2 !rounded-lg w-full text-left border border-teal-500/40 bg-teal-500/10 hover:border-teal-400 transition-colors"
        >
          <p className="text-teal-200 text-xs font-medium truncate">{header.option.label}</p>
          <p className="text-teal-300/60 text-[11px]">{header.note}</p>
        </button>
      )}

      {base.length > 8 && (
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter…"
          className="input-field !py-1.5 text-xs"
          spellCheck={false}
          autoComplete="off"
        />
      )}

      <div className="space-y-1 max-h-40 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-white/40 text-xs px-1 py-1">{emptyText}</p>
        ) : (
          filtered.slice(0, 8).map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => onPick(o)}
              className="card !p-2 !rounded-lg w-full text-left hover:border-teal-400 transition-colors"
            >
              <p className="text-white/90 text-xs font-medium truncate">{o.label}</p>
              {o.sublabel && <p className="text-white/40 text-[11px] truncate">{o.sublabel}</p>}
            </button>
          ))
        )}
      </div>

      <button type="button" onClick={onSkip} className="btn-secondary !py-1 !px-3 text-xs">
        {skipLabel}
      </button>
    </div>
  );
}

/** Confirm card — the LAST gate before the write. Buttons disable once answered. */
function ConfirmCard({
  summary, busy, disabled, onCreate, onCancel,
}: {
  summary: string;
  busy: boolean;
  disabled: boolean;
  onCreate: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="card mt-2 !p-3 space-y-2.5">
      <p className="text-white/90 text-xs leading-relaxed whitespace-pre-line">{summary}</p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCreate}
          disabled={disabled || busy}
          className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5 disabled:opacity-40"
        >
          {busy && <Loader2 size={12} className="animate-spin" />} Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled || busy}
          className="btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── Panel ──────────────────────────────────────────────────────────────────

export default function PulseBotPanel() {
  const { isOpen, close } = usePulseBot();
  const { profile } = useAuth();
  const { company } = useModules();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  // Dialog state machine: the active write dialog (null = none).
  const [dialog, setDialog] = useState<DialogState | null>(null);
  // True while the confirmed insert is in flight (locks the input + confirm card).
  const [executing, setExecuting] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const idRef = useRef(0);
  const nextId = () => ++idRef.current;

  const pushUser = (text: string) =>
    setMessages(m => [...m, { id: nextId(), role: 'user', text }]);
  const pushBot = (text: string, extra?: Omit<ChatMessage, 'id' | 'role' | 'text'>) =>
    setMessages(m => [...m, { id: nextId(), role: 'bot', text, ...extra }]);

  /**
   * Clear the transcript and start over. Also drops the LLM fallback's thread —
   * without that the model would keep replaying the old conversation as context,
   * so a "new chat" would be new only on screen, and would keep paying for
   * tokens the user thinks they discarded.
   */
  const startNewChat = () => {
    resetLlmConversation();
    setDialog(null);
    setMessages([{ id: nextId(), role: 'bot', text: GREETING_TEXT }]);
    inputRef.current?.focus();
  };

  // On open: focus the input and seed the greeting once (history persists for
  // the session so reopening keeps the conversation).
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    setMessages(prev =>
      prev.length === 0 ? [{ id: nextId(), role: 'bot', text: GREETING_TEXT }] : prev,
    );
    return () => clearTimeout(t);
  }, [isOpen]);

  // Auto-scroll to the newest message (and while "thinking" / "executing").
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking, executing]);

  // ── Dialog: lifecycle ──────────────────────────────────────────────────────

  const startCreateDeal = () => {
    setDialog({ kind: 'deal', slot: 'title', collected: {} });
    pushBot("Let's create a deal. What's the deal title?");
  };

  const startCreateTicket = () => {
    setDialog({ kind: 'ticket', slot: 'title', collected: {} });
    pushBot("Let's open a ticket. What's it about? (title)");
  };

  const startCreateInvoice = () => {
    // Role gate — invoices are finance-only (mirrors the /finance/invoices
    // ProtectedRoute). No allow → no dialog, no write.
    if (!canCreateInvoice(profile?.role)) {
      pushBot("Creating invoices is restricted to finance roles. You don't have access to that.");
      return;
    }
    setDialog({ kind: 'invoice', slot: 'client_name', collected: {} });
    pushBot("Let's create an invoice. Who's it for? (client name)");
  };

  const startCreateCompany = () => {
    setDialog({ kind: 'company', slot: 'name', collected: {} });
    pushBot("Let's add a company. What's the company name?");
  };

  const startAddContact = () => {
    setDialog({ kind: 'contact', slot: 'first_name', collected: {} });
    pushBot("Let's add a contact. What's the contact's first name?");
  };

  const startRequestLeave = () => {
    setDialog({ kind: 'leave', slot: 'leave_type', collected: {} });
    pushBot("Let's request leave. What type of leave?", { control: { kind: 'leaveType' } });
  };

  const startRequestInvite = () => {
    // Strictest gate — requesting a user invite is super_admin-only (mirrors
    // /admin/users). No allow → no dialog, no write.
    if (!canRequestInvite(profile?.role)) {
      pushBot("Requesting a user invite is restricted to admins. You don't have access to that.");
      return;
    }
    setDialog({ kind: 'invite', slot: 'email', collected: {} });
    pushBot("Let's request a user invite. What's the new user's email?");
  };

  const cancelDialog = () => {
    setDialog(null);
    setExecuting(false);
    pushBot('Okay, cancelled — nothing was created.');
  };

  // ── Dialog: text-slot answers (free-text slots + graceful nudges) ───────────

  const handleSlotText = (text: string) => {
    if (!dialog) return;

    // create_ticket: title + description are free text; priority/category are
    // pickers (nudge unless the user typed something we can't bind).
    if (dialog.kind === 'ticket') {
      switch (dialog.slot) {
        case 'title':
          setDialog({ kind: 'ticket', slot: 'description', collected: { ...dialog.collected, title: text } });
          pushBot('Add a description? (or "skip")');
          return;
        case 'description': {
          const description = isSkip(text) ? null : text;
          setDialog({ kind: 'ticket', slot: 'priority', collected: { ...dialog.collected, description } });
          pushBot('Priority?', { control: { kind: 'priority' } });
          return;
        }
        case 'priority':
          pushBot('Pick a priority using the buttons above.');
          return;
        case 'category':
          pushBot('Pick a category using the buttons above.');
          return;
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // create_invoice: every slot is free text (no pickers).
    if (dialog.kind === 'invoice') {
      switch (dialog.slot) {
        case 'client_name':
          setDialog({ kind: 'invoice', slot: 'amount', collected: { ...dialog.collected, client_name: text } });
          pushBot('Invoice amount?');
          return;
        case 'amount': {
          const n = parseFloat(text.replace(/[,\s]/g, '')); // tolerate "5,000"
          if (!Number.isFinite(n) || n <= 0) {
            pushBot('Please enter a positive amount (e.g. 1500).');
            return;
          }
          setDialog({ kind: 'invoice', slot: 'due_date', collected: { ...dialog.collected, amount: n } });
          pushBot('Due date? (YYYY-MM-DD, or "skip" for 30 days from today)');
          return;
        }
        case 'due_date': {
          // skip / empty → default to today + 30 days.
          const due_date = isSkip(text) || !text.trim() ? addDaysISODate(30) : text.trim();
          const collected = { ...dialog.collected, due_date };
          setDialog({ kind: 'invoice', slot: 'confirm', collected });
          pushBot('Ready to create this invoice:', {
            control: { kind: 'confirm', summary: summarizeInvoice(collected) },
          });
          return;
        }
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // create_company: name is free text; industry is a picker.
    if (dialog.kind === 'company') {
      switch (dialog.slot) {
        case 'name':
          setDialog({ kind: 'company', slot: 'industry', collected: { ...dialog.collected, name: text } });
          pushBot('Industry?', { control: { kind: 'industry' } });
          return;
        case 'industry':
          pushBot('Pick an industry using the buttons above.');
          return;
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // add_contact: first/last name are free text; type is a picker.
    if (dialog.kind === 'contact') {
      switch (dialog.slot) {
        case 'first_name':
          setDialog({ kind: 'contact', slot: 'last_name', collected: { ...dialog.collected, first_name: text } });
          pushBot('And the last name?');
          return;
        case 'last_name':
          setDialog({ kind: 'contact', slot: 'type', collected: { ...dialog.collected, last_name: text } });
          pushBot('Contact type?', { control: { kind: 'contactType' } });
          return;
        case 'type':
          pushBot('Pick a contact type using the buttons above.');
          return;
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // request_leave: type is a picker; dates are validated free text; the
    // end_date slot also checks end>=start and computes working days.
    if (dialog.kind === 'leave') {
      switch (dialog.slot) {
        case 'leave_type':
          pushBot('Pick a leave type using the buttons above.');
          return;
        case 'start_date': {
          if (!isValidISODate(text)) {
            pushBot('Please enter a valid start date (YYYY-MM-DD).');
            return;
          }
          setDialog({ kind: 'leave', slot: 'end_date', collected: { ...dialog.collected, start_date: text.trim() } });
          pushBot('End date? (YYYY-MM-DD)');
          return;
        }
        case 'end_date': {
          const end = text.trim();
          if (!isValidISODate(end)) {
            pushBot('Please enter a valid end date (YYYY-MM-DD).');
            return;
          }
          const start = dialog.collected.start_date ?? '';
          if (new Date(end) < new Date(start)) {
            pushBot('End date must be on or after the start date — try again (YYYY-MM-DD).');
            return;
          }
          const days = workingDays(start, end);
          if (days <= 0) {
            pushBot('That range has no working days — try again (YYYY-MM-DD).');
            return;
          }
          setDialog({ kind: 'leave', slot: 'reason', collected: { ...dialog.collected, end_date: end, days } });
          pushBot('Reason? (or "skip")');
          return;
        }
        case 'reason': {
          const reason = isSkip(text) ? null : text;
          const collected = { ...dialog.collected, reason };
          setDialog({ kind: 'leave', slot: 'confirm', collected });
          pushBot('Ready to submit this leave request:', {
            control: { kind: 'confirm', summary: summarizeLeave(collected) },
          });
          return;
        }
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // request_invite: email (validated) + full_name are free text; role +
    // department are pickers.
    if (dialog.kind === 'invite') {
      switch (dialog.slot) {
        case 'email':
          if (!isLikelyEmail(text)) {
            pushBot("That doesn't look like an email — try again.");
            return;
          }
          setDialog({ kind: 'invite', slot: 'full_name', collected: { ...dialog.collected, email: text.trim() } });
          pushBot('And their full name?');
          return;
        case 'full_name':
          setDialog({ kind: 'invite', slot: 'role', collected: { ...dialog.collected, full_name: text } });
          pushBot('Suggested role?', { control: { kind: 'role' } });
          return;
        case 'role':
          pushBot('Pick a role using the buttons above.');
          return;
        case 'department':
          if (isSkip(text)) { onDepartmentPick(null); return; }
          pushBot('Use the list above to pick a department, or click Skip.');
          return;
        case 'confirm':
          pushBot('Click Create to confirm, or Cancel.');
          return;
      }
      return;
    }

    // create_deal (existing).
    const { slot, collected } = dialog;
    switch (slot) {
      case 'title':
        setDialog({ kind: 'deal', slot: 'value', collected: { ...collected, title: text } });
        pushBot('Deal value? (type a number, or "skip")');
        return;

      case 'value': {
        let value: number | null;
        if (isSkip(text)) {
          value = null;
        } else {
          const n = parseFloat(text.replace(/[,\s]/g, '')); // tolerate "5,000"
          if (Number.isNaN(n)) {
            pushBot('Please type a number (e.g. 5000), or "skip".');
            return;
          }
          value = n;
        }
        setDialog({ kind: 'deal', slot: 'stage', collected: { ...collected, value } });
        pushBot('Which stage?', { control: { kind: 'stage' } });
        return;
      }

      // Picker slots: typed text only handles skip (where allowed); otherwise
      // nudge the user to the inline control.
      case 'stage':
        pushBot('Pick a stage using the buttons above.');
        return;
      case 'contact':
        if (isSkip(text)) { onContactPick(null); return; }
        pushBot('Use the list above to pick a contact, or click Skip.');
        return;
      case 'owner':
        if (isSkip(text)) { onOwnerSkip(); return; }
        pushBot('Use the list above to choose an owner, or click Skip.');
        return;
      case 'confirm':
        pushBot('Click Create to confirm, or Cancel.');
        return;
    }
  };

  // ── Dialog: deal picker handlers ───────────────────────────────────────────

  const onStagePick = (opt: StageOption) => {
    if (dialog?.kind !== 'deal') return;
    pushUser(opt.label);
    setDialog({ kind: 'deal', slot: 'contact', collected: { ...dialog.collected, stage: opt.value } });
    pushBot('Link a contact? (pick one, or skip)', { control: { kind: 'contact' } });
  };

  const onContactPick = (opt: PickerOption | null) => {
    if (dialog?.kind !== 'deal') return;
    pushUser(opt ? `Contact: ${opt.label}` : 'Skipped contact');
    setDialog({
      kind: 'deal',
      slot: 'owner',
      collected: { ...dialog.collected, contact_id: opt?.id ?? null, contact_label: opt?.label ?? null },
    });
    pushBot('Assign an owner? (defaults to you)', {
      control: { kind: 'owner', defaultId: profile?.id ?? null, defaultLabel: profile?.full_name ?? null },
    });
  };

  const goToDealConfirm = (collected: CollectedDeal, echo: string) => {
    pushUser(echo);
    setDialog({ kind: 'deal', slot: 'confirm', collected });
    pushBot('Ready to create this deal:', {
      control: { kind: 'confirm', summary: summarizeDeal(collected) },
    });
  };

  const onOwnerPick = (opt: PickerOption) => {
    if (dialog?.kind !== 'deal') return;
    goToDealConfirm(
      { ...dialog.collected, owner_id: opt.id, owner_label: opt.label },
      `Owner: ${opt.label}`,
    );
  };

  // Skip on the owner step accepts the default (you). With no signed-in profile
  // it falls back to an unassigned owner.
  const onOwnerSkip = () => {
    if (dialog?.kind !== 'deal') return;
    if (profile) {
      const label = `You (${profile.full_name})`;
      goToDealConfirm({ ...dialog.collected, owner_id: profile.id, owner_label: label }, `Owner: ${label}`);
    } else {
      goToDealConfirm({ ...dialog.collected, owner_id: null, owner_label: null }, 'Skipped owner');
    }
  };

  // ── Dialog: ticket picker handlers ─────────────────────────────────────────

  const onPriorityPick = (opt: PriorityOption) => {
    if (dialog?.kind !== 'ticket') return;
    pushUser(opt.label);
    setDialog({ kind: 'ticket', slot: 'category', collected: { ...dialog.collected, priority: opt.value } });
    pushBot('Category?', { control: { kind: 'category' } });
  };

  const onCategoryPick = (opt: CategoryOption) => {
    if (dialog?.kind !== 'ticket') return;
    pushUser(opt.label);
    const collected = { ...dialog.collected, category: opt.value };
    setDialog({ kind: 'ticket', slot: 'confirm', collected });
    pushBot('Ready to create this ticket:', {
      control: { kind: 'confirm', summary: summarizeTicket(collected) },
    });
  };

  // ── Dialog: company picker handler ─────────────────────────────────────────

  const onIndustryPick = (opt: IndustryOption) => {
    if (dialog?.kind !== 'company') return;
    pushUser(opt.label);
    const collected = { ...dialog.collected, industry: opt.value };
    setDialog({ kind: 'company', slot: 'confirm', collected });
    pushBot('Ready to create this company:', {
      control: { kind: 'confirm', summary: summarizeCompany(collected) },
    });
  };

  // ── Dialog: contact picker handler ─────────────────────────────────────────

  const onContactTypePick = (opt: ContactTypeOption) => {
    if (dialog?.kind !== 'contact') return;
    pushUser(opt.label);
    const collected = { ...dialog.collected, type: opt.value };
    setDialog({ kind: 'contact', slot: 'confirm', collected });
    pushBot('Ready to add this contact:', {
      control: { kind: 'confirm', summary: summarizeContact(collected) },
    });
  };

  // ── Dialog: leave picker handler ───────────────────────────────────────────

  const onLeaveTypePick = (opt: LeaveTypeOption) => {
    if (dialog?.kind !== 'leave') return;
    pushUser(opt.label);
    setDialog({ kind: 'leave', slot: 'start_date', collected: { ...dialog.collected, leave_type: opt.value } });
    pushBot('Start date? (YYYY-MM-DD)');
  };

  // ── Dialog: invite pickers (role enum + department row) ─────────────────────

  const onRolePick = (opt: RoleOption) => {
    if (dialog?.kind !== 'invite') return;
    pushUser(opt.label);
    setDialog({ kind: 'invite', slot: 'department', collected: { ...dialog.collected, suggested_role: opt.value } });
    pushBot('Department? (pick one, or skip)', { control: { kind: 'department' } });
  };

  const onDepartmentPick = (opt: PickerOption | null) => {
    if (dialog?.kind !== 'invite') return;
    pushUser(opt ? `Department: ${opt.label}` : 'Skipped department');
    const collected = {
      ...dialog.collected,
      department_id: opt?.id ?? null,
      department_label: opt?.label ?? null,
    };
    setDialog({ kind: 'invite', slot: 'confirm', collected });
    pushBot('Ready to request this invite:', {
      control: { kind: 'confirm', summary: summarizeInvite(collected) },
    });
  };

  // ── Dialog: confirm → execute the write ────────────────────────────────────

  const onConfirmCreate = async () => {
    if (!dialog || executing) return;
    const active = dialog;
    setExecuting(true);
    try {
      if (active.kind === 'deal') {
        const res = await executeCreateDeal(active.collected);
        if (res.ok) {
          pushBot(
            `✅ Created deal "${active.collected.title?.trim()}" (${stageLabel(active.collected.stage)}).`,
            { link: { to: '/crm/deals', label: 'View in Deals →' } },
          );
        } else {
          pushBot(`Couldn't create the deal: ${res.error ?? 'unknown error'}`);
        }
      } else if (active.kind === 'ticket') {
        const res = await executeCreateTicket(active.collected, profile?.id ?? null);
        if (res.ok) {
          pushBot(
            `✅ Created ticket "${active.collected.title?.trim()}" `
              + `(${priorityLabel(active.collected.priority)} · ${categoryLabel(active.collected.category)}).`,
            { link: { to: '/support/my-tickets', label: 'View in tickets →' } },
          );
        } else {
          pushBot(`Couldn't create the ticket: ${res.error ?? 'unknown error'}`);
        }
      } else if (active.kind === 'invoice') {
        const res = await executeCreateInvoice(active.collected);
        if (res.ok) {
          pushBot(
            `✅ Created invoice ${res.invoiceNumber} for "${active.collected.client_name?.trim()}" `
              + `(${res.amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}).`,
            { link: { to: '/finance/invoices', label: 'View in invoices →' } },
          );
        } else {
          pushBot(`Couldn't create the invoice: ${res.error ?? 'unknown error'}`);
        }
      } else if (active.kind === 'company') {
        const res = await executeCreateCompany(active.collected);
        if (res.ok) {
          pushBot(
            `✅ Created company "${active.collected.name?.trim()}" (${active.collected.industry ?? DEFAULT_INDUSTRY}).`,
            { link: { to: '/crm/companies', label: 'View in companies →' } },
          );
        } else {
          pushBot(`Couldn't create the company: ${res.error ?? 'unknown error'}`);
        }
      } else if (active.kind === 'contact') {
        const res = await executeAddContact(active.collected);
        if (res.ok) {
          pushBot(
            `✅ Added contact "${res.name}" (${contactTypeLabel(active.collected.type)}).`,
            { link: { to: '/crm/contacts', label: 'View in contacts →' } },
          );
        } else {
          pushBot(`Couldn't add the contact: ${res.error ?? 'unknown error'}`);
        }
      } else if (active.kind === 'leave') {
        const c = active.collected;
        const res = await executeRequestLeave(
          c,
          profile ? { id: profile.id, full_name: profile.full_name, department_id: profile.department_id } : null,
        );
        if (res.ok) {
          const days = c.days ?? 0;
          const managerClause = res.notifiedManager ? ' Your manager has been notified.' : '';
          pushBot(
            `✅ Requested ${leaveTypeLabel(c.leave_type)} leave, ${c.start_date} → ${c.end_date} `
              + `(${days} working day${days === 1 ? '' : 's'}).${managerClause}`,
            { link: { to: '/hr/my-leave', label: 'View in My Leave →' } },
          );
        } else {
          pushBot(`Couldn't submit the leave request: ${res.error ?? 'unknown error'}`);
        }
      } else {
        const c = active.collected;
        const res = await executeRequestInvite(c, profile?.id ?? null);
        if (res.ok) {
          pushBot(
            `✅ Requested an invite for "${c.full_name?.trim()}" (${c.email?.trim()}) as `
              + `${suggestedRoleLabel(c.suggested_role)}. A super_admin will complete the setup.`,
            { link: { to: '/admin/users', label: 'View in Users →' } },
          );
        } else {
          pushBot(`Couldn't request the invite: ${res.error ?? 'unknown error'}`);
        }
      }
    } catch (err) {
      console.error('[PulseBot] create execute threw', err);
      pushBot(`Couldn't create the ${active.kind}: something went wrong.`);
    } finally {
      setExecuting(false);
      setDialog(null);
    }
  };

  // ── Send (router OR dialog) ────────────────────────────────────────────────

  const send = async () => {
    const text = input.trim();
    if (!text || thinking || executing) return;

    pushUser(text);
    setInput('');

    // Active dialog: cancel works anywhere; otherwise the slot machine consumes
    // the input. The read router is bypassed entirely while a dialog is open.
    if (dialog) {
      if (isCancelInput(text)) { cancelDialog(); return; }
      handleSlotText(text);
      return;
    }

    // No dialog. Reads WIN on ambiguous phrases: a genuine read intent
    // ("my open tickets", "open tickets", "overdue invoices"…) is handled here
    // and returns. The single exception is an explicit write trigger — the read
    // router's broad 'ticket'/'tickets' keyword would otherwise swallow
    // "create ticket" / "open a ticket", so those must reach the dialog starters.
    const runReadRouter = async () => {
      setThinking(true);
      try {
        const result = await runPulseBot(text, { profile, company });
        pushBot(result.text, { items: result.items, markdown: result.markdown });
      } catch (err) {
        console.error('[PulseBot] handler threw', err);
        pushBot('Something went wrong — please try again.');
      } finally {
        setThinking(false);
      }
    };

    const isWriteTrigger =
      isCreateDealTrigger(text) || isCreateTicketTrigger(text)
      || isCreateInvoiceTrigger(text) || isCreateCompanyTrigger(text)
      || isAddContactTrigger(text) || isRequestLeaveTrigger(text)
      || isRequestInviteTrigger(text);
    if (!isWriteTrigger && matchIntent(text)) { await runReadRouter(); return; }

    // Explicit write triggers START the matching dialog.
    if (isCreateDealTrigger(text)) { startCreateDeal(); return; }
    if (isCreateTicketTrigger(text)) { startCreateTicket(); return; }
    if (isCreateInvoiceTrigger(text)) { startCreateInvoice(); return; }
    if (isCreateCompanyTrigger(text)) { startCreateCompany(); return; }
    if (isAddContactTrigger(text)) { startAddContact(); return; }
    if (isRequestLeaveTrigger(text)) { startRequestLeave(); return; }
    if (isRequestInviteTrigger(text)) { startRequestInvite(); return; }

    // Neither a read intent nor a write trigger — let the router answer (its
    // no-match fallback lists what the bot can do).
    await runReadRouter();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    } else if (e.key === 'Escape') {
      close();
    }
  };

  // A control is interactive only while it matches the dialog's current kind +
  // slot; once the dialog advances (or ends) it goes inert. The confirm card
  // stays rendered (disabled) so it remains a visible record after the write.
  const renderControl = (ctrl: BotControl) => {
    switch (ctrl.kind) {
      case 'stage':
        return dialog?.kind === 'deal' && dialog.slot === 'stage'
          ? <EnumPicker options={STAGE_OPTIONS} defaultValue={DEFAULT_STAGE} onPick={onStagePick} />
          : null;
      case 'contact':
        return dialog?.kind === 'deal' && dialog.slot === 'contact' ? (
          <RowPicker
            load={fetchContactOptions}
            onPick={onContactPick}
            onSkip={() => onContactPick(null)}
            skipLabel="Skip"
            emptyText="No contacts found."
          />
        ) : null;
      case 'owner':
        return dialog?.kind === 'deal' && dialog.slot === 'owner' ? (
          <RowPicker
            load={fetchOwnerOptions}
            onPick={onOwnerPick}
            onSkip={onOwnerSkip}
            skipLabel={ctrl.defaultId ? 'Skip (use me)' : 'Skip'}
            emptyText="No people found."
            header={
              ctrl.defaultId
                ? {
                    option: { id: ctrl.defaultId, label: `You (${ctrl.defaultLabel ?? 'me'})` },
                    note: 'Default owner',
                  }
                : undefined
            }
          />
        ) : null;
      case 'priority':
        return dialog?.kind === 'ticket' && dialog.slot === 'priority'
          ? <EnumPicker options={PRIORITY_OPTIONS} defaultValue={DEFAULT_PRIORITY} onPick={onPriorityPick} />
          : null;
      case 'category':
        return dialog?.kind === 'ticket' && dialog.slot === 'category'
          ? <EnumPicker options={CATEGORY_OPTIONS} defaultValue={DEFAULT_CATEGORY} onPick={onCategoryPick} />
          : null;
      case 'industry':
        return dialog?.kind === 'company' && dialog.slot === 'industry'
          ? <EnumPicker options={INDUSTRY_OPTIONS} defaultValue={DEFAULT_INDUSTRY} onPick={onIndustryPick} />
          : null;
      case 'contactType':
        return dialog?.kind === 'contact' && dialog.slot === 'type'
          ? <EnumPicker options={CONTACT_TYPE_OPTIONS} defaultValue={DEFAULT_CONTACT_TYPE} onPick={onContactTypePick} />
          : null;
      case 'leaveType':
        return dialog?.kind === 'leave' && dialog.slot === 'leave_type'
          ? <EnumPicker options={LEAVE_TYPE_OPTIONS} defaultValue={DEFAULT_LEAVE_TYPE} onPick={onLeaveTypePick} />
          : null;
      case 'role':
        return dialog?.kind === 'invite' && dialog.slot === 'role'
          ? <EnumPicker options={SUGGESTABLE_ROLE_OPTIONS} defaultValue={DEFAULT_SUGGESTED_ROLE} onPick={onRolePick} />
          : null;
      case 'department':
        return dialog?.kind === 'invite' && dialog.slot === 'department' ? (
          <RowPicker
            load={fetchDepartmentOptions}
            onPick={onDepartmentPick}
            onSkip={() => onDepartmentPick(null)}
            skipLabel="Skip"
            emptyText="No departments found."
          />
        ) : null;
      case 'confirm':
        return (
          <ConfirmCard
            summary={ctrl.summary}
            busy={executing}
            disabled={dialog?.slot !== 'confirm'}
            onCreate={onConfirmCreate}
            onCancel={cancelDialog}
          />
        );
    }
  };

  if (!isOpen) return null;

  const busy = thinking || executing;
  const placeholder = dialog
    ? 'Type your answer, or "cancel"…'
    : 'Type "help", or ask… e.g. "my open tickets"';

  return (
    <div
      className="fixed bottom-[88px] right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-8rem)] flex flex-col rounded-2xl overflow-hidden shadow-2xl"
      style={{
        background: 'rgba(10,15,30,0.97)',
        border: '1px solid rgba(255,255,255,0.12)',
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
          <Bot size={17} className="text-teal-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-semibold leading-tight">PulseBot</p>
          <p className="text-white/40 text-[11px]">Workspace assistant</p>
        </div>
        <button
          onClick={startNewChat}
          className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Start a new chat"
          title="New chat"
        >
          <RotateCcw size={15} />
        </button>
        <button
          onClick={close}
          className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close PulseBot"
        >
          <X size={16} />
        </button>
      </div>

      {/* Message list */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.role === 'user' ? '' : 'w-full'}`}>
              <div
                className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-teal-500 text-white rounded-br-sm'
                    : 'bg-white/10 text-white/90 rounded-bl-sm'
                }`}
              >
                {msg.markdown ? <Markdown content={msg.text} /> : msg.text}
              </div>

              {msg.items && msg.items.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {msg.items.map((item, i) => (
                    <ResultItem key={i} item={item} onNavigate={close} />
                  ))}
                </div>
              )}

              {msg.control && renderControl(msg.control)}

              {msg.link && (
                <Link
                  to={msg.link.to}
                  onClick={close}
                  className="inline-block mt-2 text-xs font-medium text-teal-300 hover:text-teal-200"
                >
                  {msg.link.label}
                </Link>
              )}
            </div>
          </div>
        ))}

        {/* Thinking / executing indicator */}
        {busy && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl rounded-bl-sm bg-white/10 text-white/50 flex items-center gap-2 text-sm">
              <Loader2 size={14} className="animate-spin" />
              {executing ? 'Creating…' : 'Thinking…'}
            </div>
          </div>
        )}
      </div>

      {/* Input row */}
      <div className="px-3 py-3 border-t border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="input-field flex-1 !py-2 text-sm"
            spellCheck={false}
            autoComplete="off"
            disabled={busy}
          />
          <button
            onClick={send}
            disabled={!input.trim() || busy}
            className="btn-primary !px-3 !py-2 flex items-center justify-center disabled:opacity-40"
            aria-label="Send"
          >
            <Send size={15} />
          </button>
        </div>
        <p className="text-white/25 text-[10px] mt-1.5 flex items-center gap-1">
          <Sparkles size={10} /> Deterministic · confirms before any write
        </p>
      </div>
    </div>
  );
}
